import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { getDomainConfig } from '../config/domains';

// Socket.IO instance
let socket = null;

// Event callbacks
const eventCallbacks = {
  'new-message': [],
  'update-message': [],
  'delete-message': [],
  'new-direct-message': [],
  'new-team-channel': [],
  'channel-locked': [],
  'channel-unlocked': [],
  'channel-deleted': [],
  'mention': [],
  'messages-read': [],
  'connect': [],
  'disconnect': [],
  'error': []
};

/**
 * Initialize Socket.IO connection
 */
export const initSocket = () => {
  if (socket) return socket;
  
  // Get the current domain configuration
  const { API } = getDomainConfig();
  
  try {
    // Create socket connection
    socket = io(API, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      timeout: 20000,
      autoConnect: true,
      forceNew: true,
      secure: window.location.hostname !== 'localhost',
      rejectUnauthorized: false
    });
    
    // Set up default event listeners
    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
      eventCallbacks['connect'].forEach(callback => callback());
    });
    
    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      eventCallbacks['disconnect'].forEach(callback => callback());
    });
    
    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      console.log('Falling back to long polling if needed');
      eventCallbacks['error'].forEach(callback => callback(error));
    });
    
    // Set up custom event listeners
    socket.on('new-message', (data) => {
      try {
        // Validate data before passing to handlers
        if (!data || !data.message) {
          console.error('Invalid message data received:', data);
          return;
        }
        eventCallbacks['new-message'].forEach(callback => {
          try {
            callback(data);
          } catch (error) {
            console.error('Error in new-message callback:', error);
          }
        });
      } catch (error) {
        console.error('Error processing new-message event:', error);
      }
    });
    
    socket.on('update-message', (data) => {
      try {
        if (!data || !data.message) {
          console.error('Invalid update-message data received:', data);
          return;
        }
        eventCallbacks['update-message'].forEach(callback => {
          try {
            callback(data);
          } catch (error) {
            console.error('Error in update-message callback:', error);
          }
        });
      } catch (error) {
        console.error('Error processing update-message event:', error);
      }
    });
    
    socket.on('delete-message', (data) => {
      try {
        if (!data || !data.messageId) {
          console.error('Invalid delete-message data received:', data);
          return;
        }
        eventCallbacks['delete-message'].forEach(callback => {
          try {
            callback(data);
          } catch (error) {
            console.error('Error in delete-message callback:', error);
          }
        });
      } catch (error) {
        console.error('Error processing delete-message event:', error);
      }
    });
    
    // Custom client-side event for marking messages as read
    socket.on('client-mark-read', (data) => {
      console.log('Client requested mark-read:', data);
      // Trigger the messages-read handlers with the same data structure
      eventCallbacks['messages-read'].forEach(callback => callback(data));
    });
    
    socket.on('mention', (data) => {
      console.log('Mention received via socket:', data);
      try {
        // Ensure the message data is properly structured
        if (!data || !data.message || !data.channelId) {
          console.error('Invalid mention data received:', data);
          return;
        }
        
        eventCallbacks['mention'].forEach(callback => callback(data));
      } catch (error) {
        console.error('Error processing mention event:', error);
      }
    });
    
    socket.on('messages-read', (data) => {
      console.log('Messages read status updated via socket:', data);
      try {
        // Ensure the data is properly structured
        if (!data || !data.channelId || !data.userId) {
          console.error('Invalid messages-read data received:', data);
          return;
        }
        
        eventCallbacks['messages-read'].forEach(callback => callback(data));
      } catch (error) {
        console.error('Error processing messages-read event:', error);
      }
    });
    
    socket.on('new-direct-message', (data) => {
      console.log('New direct message channel received via socket:', data);
      try {
        // Ensure the channel data is properly structured
        if (!data || !data.channel) {
          console.error('Invalid direct message data received:', data);
          return;
        }
        
        // Make sure channel has members array
        if (!data.channel.members || !Array.isArray(data.channel.members)) {
          console.error('Invalid channel members in direct message data:', data);
          data.channel.members = [];
          return;
        }
        
        // Normalize the member IDs (some might be objects, some strings)
        data.channel.members = data.channel.members.map(member => {
          if (!member) return { _id: 'unknown' };
          
          if (typeof member === 'string') {
            return { _id: member };
          }
          return member;
        });
        
        eventCallbacks['new-direct-message'].forEach(callback => {
          try {
            callback(data);
          } catch (error) {
            console.error('Error in new-direct-message callback:', error);
          }
        });
      } catch (error) {
        console.error('Error processing direct message event:', error);
      }
    });
    
    socket.on('new-team-channel', (data) => {
      console.log('New team channel received via socket:', data);
      try {
        // Ensure the channel data is properly structured
        if (!data || !data.channel) {
          console.error('Invalid team channel data received:', data);
          return;
        }
        
        eventCallbacks['new-team-channel'].forEach(callback => callback(data));
      } catch (error) {
        console.error('Error processing team channel event:', error);
      }
    });
    
    socket.on('channel-locked', (data) => {
      console.log('Channel locked event received via socket:', data);
      try {
        // Ensure the channel data is properly structured
        if (!data || !data.channel || !data.lockedBy) {
          console.error('Invalid channel-locked data received:', data);
          return;
        }
        
        eventCallbacks['channel-locked'].forEach(callback => callback(data));
      } catch (error) {
        console.error('Error processing channel-locked event:', error);
      }
    });
    
    socket.on('channel-unlocked', (data) => {
      console.log('Channel unlocked event received via socket:', data);
      try {
        // Ensure the channel data is properly structured
        if (!data || !data.channel || !data.unlockedBy) {
          console.error('Invalid channel-unlocked data received:', data);
          return;
        }
        
        eventCallbacks['channel-unlocked'].forEach(callback => callback(data));
      } catch (error) {
        console.error('Error processing channel-unlocked event:', error);
      }
    });
    
    socket.on('channel-deleted', (data) => {
      console.log('Channel deleted event received via socket:', data);
      try {
        // Ensure the data is properly structured
        if (!data || !data.channelId || !data.deletedBy) {
          console.error('Invalid channel-deleted data received:', data);
          return;
        }
        
        eventCallbacks['channel-deleted'].forEach(callback => callback(data));
      } catch (error) {
        console.error('Error processing channel-deleted event:', error);
      }
    });
    
    // If socket is already connected, trigger the connect callbacks immediately
    if (socket.connected) {
      console.log('Socket already connected on init:', socket.id);
      setTimeout(() => {
        eventCallbacks['connect'].forEach(callback => callback());
      }, 0);
    }
    
    return socket;
  } catch (error) {
    console.error('Failed to connect to socket:', error);
    toast.error('Failed to connect to real-time updates');
    return null;
  }
};

/**
 * Join a channel room to receive its messages
 * @param {string} channelId - The channel ID to join
 */
export const joinChannel = (channelId) => {
  if (!socket || !socket.connected) {
    initSocket();
  }
  
  if (socket && channelId) {
    console.log('Joining channel:', channelId);
    socket.emit('join-channel', channelId);
  }
};

/**
 * Leave a channel room
 * @param {string} channelId - The channel ID to leave
 */
export const leaveChannel = (channelId) => {
  if (socket && channelId) {
    console.log('Leaving channel:', channelId);
    socket.emit('leave-channel', channelId);
  }
};

/**
 * Join a user-specific room for direct message notifications
 * @param {string} userId - The user ID
 */
export const joinUserRoom = (userId) => {
  if (!socket || !socket.connected) {
    initSocket();
  }
  
  if (socket && userId) {
    console.log('Joining user room:', userId);
    socket.emit('join-user', userId);
  }
};

/**
 * Add event listener
 * @param {string} event - Event name
 * @param {function} callback - Callback function
 */
export const addEventListener = (event, callback) => {
  if (eventCallbacks[event]) {
    eventCallbacks[event].push(callback);
  }
};

/**
 * Remove event listener
 * @param {string} event - Event name
 * @param {function} callback - Callback function to remove
 */
export const removeEventListener = (event, callback) => {
  if (eventCallbacks[event]) {
    eventCallbacks[event] = eventCallbacks[event].filter(cb => cb !== callback);
  }
};

/**
 * Disconnect socket
 */
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

/**
 * Check if socket is connected
 * @returns {boolean} True if connected, false otherwise
 */
export const isConnected = () => {
  return socket && socket.connected;
};

/**
 * Mark messages in a channel as read (client-side notification)
 * @param {string} channelId - The channel ID
 * @param {string} userId - The user ID who read the messages
 */
export const emitMessagesRead = (channelId, userId) => {
  if (!socket || !socket.connected) {
    initSocket();
  }
  
  if (socket && channelId && userId) {
    console.log('Emitting client-mark-read event:', { channelId, userId });
    socket.emit('client-mark-read', { channelId, userId });
  }
};

export default {
  initSocket,
  joinChannel,
  leaveChannel,
  joinUserRoom,
  addEventListener,
  removeEventListener,
  disconnectSocket,
  isConnected,
  emitMessagesRead
}; 